<template>
  <div class="p-6">
    <h1 class="text-2xl mb-4">Usage Logs</h1>
    <UsageList @create="openCreate" @view="openView" @edit="openEdit" @delete="confirmDelete" />

    <div v-if="showForm">
      <div class="modal-backdrop fixed inset-0 bg-black/50" @click="closeForm"></div>
      <div class="modal-content fixed inset-0 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">{{ editing ? 'Edit Usage' : 'New Usage' }}</h3>
            <button @click="closeForm" class="text-gray-500">Close</button>
          </div>
          <UsageLogForm :initial="editingItem" @save="onSave" @cancel="closeForm" />
        </div>
      </div>
    </div>

    <div v-if="showDetail">
      <div class="drawer-backdrop fixed inset-0 bg-black/40" @click="closeDetail"></div>
      <div
        class="drawer fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-lg p-6 overflow-auto"
      >
        <button @click="closeDetail" class="mb-4 text-gray-600">Close</button>
        <UsageDetail
          v-if="selectedLog"
          :log="selectedLog"
          @edit="openEditFromDetail"
          @delete="confirmDelete"
        />
      </div>
    </div>

    <ConfirmDialog
      v-if="confirming"
      :title="confirmTitle"
      :message="confirmMessage"
      @confirm="doDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import UsageList from '@/components/usage/UsageList.vue'
import UsageDetail from '@/components/usage/UsageDetail.vue'
import UsageLogForm from '@/components/usage/UsageLogForm.vue'
import { useUsageStore } from '@/stores/usageStore'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import type { UsageLog, UsageLogCreate } from '@/types/usage'

const usageStore = useUsageStore()

const showForm = ref(false)
const showDetail = ref(false)
const editing = ref(false)
const selectedId = ref<number | null>(null)
const confirming = ref(false)
const confirmTarget = ref<number | null>(null)
const confirmTitle = ref('Delete usage log')
const confirmMessage = ref('Are you sure you want to delete this usage log?')
const editingItem = ref<UsageLog | null>(null)

onMounted(async () => {
  await usageStore.fetchUsageLogs()
})

const selectedLog = computed(() => {
  if (!selectedId.value) return null
  return usageStore.usageLogs.find((l) => l.id === selectedId.value) || null
})

function openCreate() {
  editing.value = false
  editingItem.value = null
  showForm.value = true
}

function openEdit(id: number) {
  const item = usageStore.usageLogs.find((l) => l.id === id)
  if (item) {
    editing.value = true
    editingItem.value = { ...item }
    showForm.value = true
  }
}

function openEditFromDetail(id: number) {
  showDetail.value = false
  openEdit(id)
}

function openView(id: number) {
  selectedId.value = id
  showDetail.value = true
}

function closeForm() {
  showForm.value = false
}
function closeDetail() {
  showDetail.value = false
}

async function onSave(payload: UsageLogCreate) {
  if (editing.value && editingItem.value?.id) {
    await usageStore.updateUsageLog(editingItem.value.id, payload)
  } else {
    await usageStore.createUsageLog(payload)
  }
  showForm.value = false
  await usageStore.fetchUsageLogs()
}

async function confirmDelete(id: number) {
  confirmTarget.value = id
  confirming.value = true
}

async function doDelete() {
  if (!confirmTarget.value) return
  await usageStore.deleteUsageLog(confirmTarget.value)
  await usageStore.fetchUsageLogs()
  if (selectedId.value === confirmTarget.value) selectedId.value = null
  confirming.value = false
  confirmTarget.value = null
}

function cancelDelete() {
  confirming.value = false
  confirmTarget.value = null
}
</script>
